/**
 * @file src/permissions/desks.js
 * @description Which operator surfaces belong to which desk, and which desks
 * each owner-group role sits at. One table, read by the dashboard sidebar,
 * the dashboard's route guard and the gateway.
 *
 * ── Why this exists ──────────────────────────────────────────────────
 *
 * Walked 2026-09-11 as support-op@moksy.local (role `support`): the sidebar
 * offered Permissions, Captcha, the API gateway console, AI models, Billing,
 * Subscriptions and every other service control panel — the same menu as an
 * admin. Nearly every operator row was declared `roles: OWNER_ROLES`, and
 * `requireAdminRole` on the gateway admits any owner-group role, so the menu
 * and the door agreed on the wrong answer: "is this person staff?" was the
 * only question either could ask.
 *
 * The right question is "does this person sit at the desk that does this?".
 * docs/MOKSY_STAFF_OPERATIONS.md defines the 32 desks; scripts/journeys/staff
 * holds them as data, and seats.mjs unflattens each of the twenty owner roles
 * into the desks it always meant (`support` = the Support desk,
 * `content_moderator` = Trust and safety, …). This file is the part of that
 * model the running product needs, in the package both the browser and the
 * services already import.
 *
 * ── What reads it ────────────────────────────────────────────────────
 *
 *   apps/dashboard/src/hooks/useDynamicCatalog.js   every operator row's
 *                                                   `roles` is derived here
 *   apps/dashboard/src/routes/AdminRoute.jsx        refuses a typed URL the
 *                                                   same way the menu hides it
 *   foundation/gateway/src/middlewares/security.js  `requireDesk(...)`
 *   scripts/journeys/staff/seats.mjs                asserts its OWNER_MAP
 *                                                   desks agree with ROLE_DESKS
 *   scripts/journeys/staff/desks.mjs                a desk's `surfaces` are
 *                                                   read from SURFACE_DESKS
 *
 * ── What it is NOT ───────────────────────────────────────────────────
 *
 * Not the scope table. `requirePermission(slug)` still decides what a role may
 * DO on a surface (foundation/permissions PermissionService). A desk decides
 * which surfaces a role is shown and may open at all; the scope then decides
 * which buttons on it work. Both must pass.
 *
 * Person-level desk seats (User.person.responsibilities.deskSeats) are not
 * read here yet. The gateway only sees token claims, and a seat that widened
 * the menu without widening the door would put a link in front of somebody
 * that 403s. They join when the login token carries them — both sides at once.
 */

import { OWNER_ROLES } from './ownerRoles.js';

// The 32 desks, keyed as in scripts/journeys/staff/desks.mjs. Labels only —
// duties, rungs and cover stay in the journeys data, which is not shipped.
export const DESK_LABELS = Object.freeze({
  SUP: 'Support',
  AMG: 'Account management',
  ONB: 'Onboarding',
  RVO: 'Reviewer operations',
  ACR: 'Accreditation',
  QAD: 'Quality',
  DSP: 'Disputes',
  PAY: 'Payouts',
  BIL: 'Billing',
  TRE: 'Treasury',
  TSF: 'Trust and safety',
  FRD: 'Fraud and abuse',
  SEC: 'Security',
  PRV: 'Privacy',
  LEG: 'Legal and compliance',
  KBS: 'Knowledge stewardship',
  TAX: 'Taxonomy and schema',
  PAT: 'Patterns and models',
  TPL: 'Templates and configuration',
  LRN: 'Learning',
  CNT: 'Content',
  LOC: 'Markets and localisation',
  OPS: 'Platform operations',
  REL: 'Release',
  INC: 'Incident command',
  ANL: 'Analytics',
  GRW: 'Growth',
  SAL: 'Sales',
  PRT: 'Partnerships',
  PEO: 'People operations',
  ACS: 'Access administration',
  EXE: 'Executive'
});

export const DESK_KEYS = Object.freeze(Object.keys(DESK_LABELS));

/**
 * The two roles that are a tier of authority over every desk rather than a
 * desk (seats.mjs marks them `*`). They see and open every operator surface.
 */
export const AUTHORITY_ROLES = Object.freeze(['super_admin', 'admin']);

/**
 * The other eighteen owner roles, unflattened into their desks. Must agree
 * with the `desks` column of OWNER_MAP in scripts/journeys/staff/seats.mjs —
 * seats.mjs asserts it at import, so a disagreement fails the register build.
 */
export const ROLE_DESKS = Object.freeze({
  chief_operating_officer:  ['EXE', 'OPS'],
  chief_financial_officer:  ['EXE', 'TRE'],
  chief_security_officer:   ['EXE', 'SEC'],
  chief_compliance_officer: ['EXE', 'LEG'],
  head_of_platform:         ['EXE', 'OPS'],
  head_of_support:          ['SUP'],
  manager:                  ['PEO'],
  operations_manager:       ['OPS', 'REL'],
  finance_controller:       ['PAY', 'TRE'],
  finance:                  ['PAY', 'BIL'],
  auditor:                  ['QAD', 'SEC'],
  data_protection_officer:  ['PRV'],
  incident_responder:       ['INC', 'OPS'],
  release_manager:          ['REL'],
  reviewer_coordinator:     ['RVO', 'ACR'],
  support_lead:             ['SUP'],
  support:                  ['SUP'],
  content_moderator:        ['TSF']
});

/** Every desk — the landing console, which each seated operator needs. */
export const ANY_DESK = '*';
/** No desk: admin and super_admin only, because the server says so today. */
export const AUTHORITY_ONLY = '-';
/** super_admin alone — the gateway refuses even admin. */
export const SUPER_ONLY = '!';

/**
 * Operator surface → who may open it TODAY, and who the desk model says
 * should. Dashboard paths (not API paths). Longest matching prefix wins, so a
 * sub-page can differ from its console: `/admin/downloads/control/active-links`
 * is narrower than the downloads console it sits in.
 *
 *   open     the desks whose roles the server lets through right now — `*`
 *            any seated operator, `-` admin and super_admin only, `!`
 *            super_admin only. This is what the sidebar and AdminRoute read,
 *            so a row is never offered to somebody the door refuses.
 *   pending  desks the model (docs/MOKSY_STAFF_OPERATIONS.md) says work this
 *            surface but the server does not admit yet — mostly a downstream
 *            service that still asks `role === 'admin'`. Grants nothing. It is
 *            the list of doors to open, each one a decision, not a default.
 *
 * Audited 2026-09-11 against foundation/gateway/src/routes/v1.js and each
 * service's own route guard. Where the gateway carries `requireDesk(...)`
 * (disputes, knowledge, jobs, events, orchestrator) the open column IS the
 * gate; elsewhere it records what the scope and service checks already allow.
 *
 * Ordered as the sidebar is, so the two can be read side by side.
 *
 * path prefix | open | pending | why
 */
const SURFACE_TABLE = `
/admin/console|*||every seated operator lands here; its tiles are filtered by this table
/admin/reports|-|ANL,EXE|downloads-service requireAdminAuth is admin-only
/requirements/v2/admin|-|TPL|requirements v2 adminOnly
/requirements/v2/admin/feature-flags|-|REL,TPL,PAT|FeatureFlagService isPlatformAdmin
/requirements/v2/admin/audit-log|-|SEC,QAD,TPL|isPlatformStaff: staff/manager/admin only
/requirements/v2/admin/queues|-|*|withdrawals, disputes and applications are each admin-only
/requirements/v2/admin/two-person|-|*|
/templates|-|TPL|
/functions|-|PAT|
/patterns|-|PAT|
/knowledge/schemas|TAX||gateway requireDesk('TAX')
/knowledge/project-fields|-|TAX|
/configurations|-|TPL,REL|
/knowledge|KBS||gateway requireDesk('KBS') on writes
/knowledge/output-reports|KBS|TPL|
/requirements/v2/admin/knowledge-base|-|KBS|
/requirements/v2/admin/examples|-|KBS,LRN|
/requirements/v2/admin/journeys|-|TPL|
/requirements/v2/admin/workflow-version|-|TPL|
/requirements/v2/admin/workflow-templates|-|TPL|
/learning|-|LRN|LearningController isPlatformAdmin
/requirements/v2/admin/who-was-wrong|-|LRN|
/requirements/v2/admin/proposal-clusters|-|LRN|
/requirements/v2/admin/review|-|LRN|
/requirements/v2/admin/experiment|-|LRN|
/requirements/v2/admin/experiment-runs|-|LRN|
/admin/review-pipeline|-|RVO,QAD,DSP|ReviewService admin-only
/admin/reviews/settings|-|RVO|review.admin.read is held by no staff role
/requirements/v2/admin/disputes|DSP||gateway requireDesk('DSP') — any owner could resolve and refund before
/requirements/v2/admin/expert-applications|-|RVO,ACR|ExpertService isPlatformAdmin
/admin/reviewer-earnings|-|PAY,RVO|
/admin/commons|-|TRE,LEG,EXE|CommonsController isPlatformAdmin
/admin/reviewer-plans|-|RVO,BIL|
/admin/calibration|ACR,RVO|LRN|users-service owner reads, users.identity.update writes
/admin/reviewer-promotions|RVO||users.identity.update — reviewer_coordinator holds it
/admin/enquiries|-|SUP,SAL|communications admin routes are admin-only
/admin/reviewer-applications|RVO,ACR||
/admin/identity|SUP,ACS,PEO,SEC,FRD,TSF,PRV,QAD||users.identity.read is held by every staff role
/admin/users-admin|SUP,ACS,SEC,PEO||
/admin/eligibility-matrix|ACR,RVO||progression.eligibility.manage
/admin/scoring-weights|RVO,ACR||static page
/admin/auth|-|SEC,ACS|auth-service admin routes are admin-only
/admin/permissions|-|ACS|permissions-service routes are admin-only
/admin/captcha|!|FRD,SEC|gateway requireSuperAdminRole
/admin/projects/control|-|SUP,TSF,OPS|projects-service admin routes are admin-only
/admin/requirements|OPS|SUP|system.settings.read
/admin/requirements/assessment-taxonomy|-|TAX|
/admin/platform-settings|!|REL|gateway requireSuperAdminRole
/admin/preview/control|-|OPS|
/admin/registry/control|OPS|PRT,TPL|registry-service owner-group
/admin/dictionary/control|-|KBS,CNT|dictionary settings capability
/admin/dictionary/control/data-types|-|TAX,KBS|
/admin/dictionary/control/templates|-|TPL,KBS|
/admin/dictionary/control/settings|-|CNT,LOC|
/admin/storage/control|-|OPS|storage-service admin-only
/admin/storage/control/flagged|-|OPS,TSF|
/admin/ai-llm/control|-|PAT,OPS|ai-llm-service admin-only
/admin/ai-memory/control|-|PAT,OPS|
/admin/ai-policy/control|-|PAT,PRV,SEC|
/admin/concierge|-|AMG,SAL,SUP|payments-service admin-only
/admin/auth-providers|SEC|ACS|system.settings.read reads; writes super_admin
/admin/audit/control|SEC,QAD|PRV|system.settings.read — the DPO does not hold it
/admin/audit/control/suspicious|SEC|FRD|
/admin/events/control|OPS||gateway requireDesk('OPS')
/admin/notifications/control|-|OPS,GRW|notifications-service admin-only
/admin/communications/control|-|OPS,SUP,TSF,GRW,CNT|
/admin/orchestrator/control|OPS||gateway requireDesk('OPS')
/admin/jobs/control|OPS||gateway requireDesk('OPS')
/admin/downloads/control|-|OPS,SUP|downloads-service admin-only
/admin/gateway/control|OPS||system.settings.read reads; writes super_admin
/billing/payments|-|PAY,BIL,DSP|subscriptions.admin.read is held by no staff role
/admin/payments|-|PAY,BIL|
/admin/payments/disputes|-|DSP,PAY|
/admin/payments/control/disputes|-|DSP,PAY|
/admin/payments/settings|-|TRE|
/admin/subscriptions/control|-|BIL|
/admin/billing/control|-|BIL|
`;

const readDesks = (cell, prefix) => {
  const v = String(cell || '').trim();
  if (!v) return [];
  if (v === ANY_DESK || v === AUTHORITY_ONLY || v === SUPER_ONLY) return [v];
  const list = v.split(',').map((d) => d.trim());
  for (const d of list) {
    if (!DESK_LABELS[d]) throw new Error(`surface ${prefix}: unknown desk ${d}`);
  }
  return list;
};

export const SURFACE_DESKS = Object.freeze(SURFACE_TABLE.trim().split('\n').map((line) => {
  const [prefix, open, pending, why] = line.split('|');
  return Object.freeze({
    prefix,
    desks: Object.freeze(readDesks(open, prefix)),
    pending: Object.freeze(readDesks(pending, prefix)),
    why: why || null
  });
}));

// Fail at import, not at the first refused click: every role is placed, and
// every desk a role names exists.
{
  const seen = new Set();
  for (const s of SURFACE_DESKS) {
    if (seen.has(s.prefix)) throw new Error(`surface listed twice: ${s.prefix}`);
    seen.add(s.prefix);
  }
  for (const [role, desks] of Object.entries(ROLE_DESKS)) {
    if (!OWNER_ROLES.includes(role)) throw new Error(`${role}: not an owner role`);
    for (const d of desks) if (!DESK_LABELS[d]) throw new Error(`${role}: unknown desk ${d}`);
  }
  // A staff role added to ownerRoles.js with no desk would be shown nothing
  // and refused everywhere — say so at import rather than on their first day.
  const unplaced = OWNER_ROLES.filter((r) => !AUTHORITY_ROLES.includes(r) && !ROLE_DESKS[r]);
  if (unplaced.length) throw new Error(`owner roles with no desk: ${unplaced.join(', ')}`);
}

const norm = (v) => String(v || '').trim().toLowerCase();
const pathOf = (path) => String(path || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/';

/** True for the roles that hold authority over every desk. */
export const isAuthorityRole = (role) => AUTHORITY_ROLES.includes(norm(role));

/** The desks a role sits at. `['*']` for an authority role; `[]` for anyone else. */
export const desksForRole = (role) => {
  const r = norm(role);
  if (AUTHORITY_ROLES.includes(r)) return [ANY_DESK];
  return ROLE_DESKS[r] ? [...ROLE_DESKS[r]] : [];
};

/**
 * The table row covering a dashboard path, by longest matching prefix on
 * whole segments (`/admin/payments` does not cover `/admin/payments-x`).
 * Null when no operator surface covers it.
 */
export const surfaceFor = (path) => {
  const p = pathOf(path);
  let best = null;
  for (const s of SURFACE_DESKS) {
    if (p === s.prefix || p.startsWith(`${s.prefix}/`)) {
      if (!best || s.prefix.length > best.prefix.length) best = s;
    }
  }
  return best;
};

/** The desks that work a path, or null when the path is not an operator surface. */
export const desksForSurface = (path) => {
  const s = surfaceFor(path);
  return s ? [...s.desks] : null;
};

/** Does a role sit at any of these desks? Authority roles sit at all of them. */
export const roleSitsAt = (role, desks = []) => {
  if (desks.includes(SUPER_ONLY)) return norm(role) === 'super_admin';
  const held = desksForRole(role);
  if (held.includes(ANY_DESK)) return true;
  if (!held.length) return false;
  if (desks.includes(ANY_DESK)) return true;
  return desks.some((d) => held.includes(d));
};

/**
 * May this role open this dashboard path?
 *
 * `null` when the path is not in the table — the caller decides, because an
 * unlisted path means different things to a menu (fail closed: nobody seated
 * is shown it) and to a route guard wrapping detail pages the table does not
 * enumerate (defer to the owner-group check and the server).
 */
export const maySeeSurface = (role, path) => {
  const desks = desksForSurface(path);
  if (!desks) return null;
  return roleSitsAt(role, desks);
};

/** Every owner role that may open a path — what a nav row's `roles` becomes. */
export const rolesForSurface = (path, ownerRoles = OWNER_ROLES) => {
  const desks = desksForSurface(path);
  if (!desks) return [...AUTHORITY_ROLES];
  return ownerRoles.filter((r) => roleSitsAt(r, desks));
};

/** The desks' labels, for a refusal that names what the page needs. */
export const deskLabels = (desks = []) => desks
  .filter((d) => d !== ANY_DESK && d !== AUTHORITY_ONLY && d !== SUPER_ONLY)
  .map((d) => DESK_LABELS[d] || d);

export default {
  DESK_LABELS, DESK_KEYS, AUTHORITY_ROLES, ROLE_DESKS, SURFACE_DESKS, ANY_DESK, AUTHORITY_ONLY, SUPER_ONLY,
  desksForRole, desksForSurface, surfaceFor, roleSitsAt, maySeeSurface, rolesForSurface, deskLabels, isAuthorityRole
};
